import { useEffect, useState, useRef } from "react";
import { Bell, X, Check, CheckCheck } from "lucide-react";
import { api } from "@/api/client";
import { useNavigate } from "react-router-dom";
import { fmtDate } from "@/components/common";
import { toast } from "sonner";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [wsConnected, setWsConnected] = useState(false);
  const ref = useRef(null);
  const wsRef = useRef(null);
  const navigate = useNavigate();

  const loadCount = async () => {
    try {
      const r = await api.get("/notifications/unread-count");
      setUnread(r.data?.data?.count || 0);
    } catch {}
  };

  const loadItems = async () => {
    try {
      const r = await api.get("/notifications", { params: { limit: 20 } });
      setItems(r.data?.data || []);
    } catch {}
  };

  // WebSocket: live push
  useEffect(() => {
    const token = localStorage.getItem("t360_access_token");
    if (!token) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backend.replace(/^https:/, "wss:").replace(/^http:/, "ws:") + `/api/ws/notifications?token=${encodeURIComponent(token)}`;
    let reconnectTimeout = null;
    let pingInterval = null;
    let stopped = false;

    const connect = () => {
      if (stopped) return;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setWsConnected(true);
        // Start ping every 30s to keep connection alive
        pingInterval = setInterval(() => {
          if (ws.readyState === 1) ws.send("ping");
        }, 30000);
      };

      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "notification" && msg.data) {
            setItems((xs) => [msg.data, ...xs].slice(0, 20));
            setUnread((n) => n + 1);
            // Toast notification
            toast(msg.data.title, { description: msg.data.body });
          }
        } catch {}
      };

      ws.onclose = () => {
        setWsConnected(false);
        if (pingInterval) clearInterval(pingInterval);
        if (!stopped) reconnectTimeout = setTimeout(connect, 3000);
      };

      ws.onerror = () => {};
    };

    connect();
    return () => {
      stopped = true;
      if (pingInterval) clearInterval(pingInterval);
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
      if (wsRef.current && wsRef.current.readyState === 1) wsRef.current.close();
    };
  }, []);

  // Polling fallback every 60s
  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (open) loadItems();
  }, [open]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/mark-read`);
    setItems((xs) => xs.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
    setUnread((n) => Math.max(0, n - 1));
  };

  const markAll = async () => {
    await api.post("/notifications/mark-all-read");
    setItems((xs) => xs.map((x) => ({ ...x, is_read: true })));
    setUnread(0);
  };

  const onClick = async (n) => {
    if (!n.is_read) await markRead(n.id);
    setOpen(false);
    if (n.task_code || n.task_id) {
      navigate(`/tasks`);
    }
  };

  const typeColor = (t) => ({
    approval: "bg-violet-50 text-violet-700 border-violet-200",
    task: "bg-blue-50 text-blue-700 border-blue-200",
    return: "bg-yellow-50 text-yellow-700 border-yellow-200",
    rework: "bg-orange-50 text-orange-700 border-orange-200",
    escalation: "bg-red-50 text-red-700 border-red-200",
  }[t] || "bg-slate-50 text-slate-700 border-slate-200");

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="p-1.5 hover:bg-slate-100 rounded-sm transition-colors relative"
        data-testid="notifications-btn"
        title={wsConnected ? "Live notifications connected" : "Notifications (offline)"}
      >
        <Bell size={16} className="text-slate-600" />
        {wsConnected && (
          <span className="absolute -bottom-0.5 -right-0.5 w-2 h-2 bg-emerald-500 border border-white rounded-full" data-testid="ws-indicator" />
        )}
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-600 text-white text-[9px] font-bold rounded-full flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed left-2 right-2 top-14 md:absolute md:left-auto md:right-0 md:top-auto md:mt-2 md:w-96 bg-white border border-slate-200 rounded-sm shadow-lg z-50 max-h-[80vh] md:max-h-[600px] flex flex-col"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div>
              <div className="font-heading font-semibold text-slate-900 text-sm flex items-center gap-2">
                Notifications
                {wsConnected && <span className="badge-base bg-emerald-50 text-emerald-700 border-emerald-200 normal-case">Live</span>}
              </div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{unread} unread</div>
            </div>
            <div className="flex gap-1">
              {unread > 0 && (
                <button
                  onClick={markAll}
                  className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 px-2 py-1 hover:bg-slate-100 rounded-sm"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck size={12} /> Mark all
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-100 rounded-sm">
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto flex-1">
            {items.length === 0 ? (
              <div className="text-center py-12 text-sm text-slate-500">No notifications</div>
            ) : (
              items.map((n) => (
                <div
                  key={n.id}
                  className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${
                    !n.is_read ? "bg-[#334E7B]/30" : ""
                  }`}
                  onClick={() => onClick(n)}
                  data-testid={`notification-${n.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`badge-base ${typeColor(n.type)}`}>{n.type}</span>
                        {!n.is_read && <span className="w-1.5 h-1.5 rounded-full bg-[#334E7B]"></span>}
                      </div>
                      <div className="text-sm font-medium text-slate-900 truncate">{n.title}</div>
                      <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.body}</div>
                      <div className="text-[10px] text-slate-400 mt-1">{fmtDate(n.created_at)}</div>
                    </div>
                    {!n.is_read && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                        className="p-1 text-slate-400 hover:text-slate-700"
                        title="Mark read"
                      >
                        <Check size={12} />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
